import React, { useEffect, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LandingView } from "./views/LandingView";
import { PublicHeader } from "./components/layout/PublicHeader";
import { ErrorFallback } from "./components/ui/ErrorFallback";
import { isAdminHost, isShopHost, isDevEnvironment } from "./utils/domain";
import { siteAnalytics } from "./services/siteAnalytics";
import { adPixels } from "./services/adPixels";
import { API_BASE } from "./config/api";
import "./index.css";

// Chargement différé (code-splitting) uniquement du tableau de bord lourd et de l'admin
const App = lazy(() => import("./App"));
const AdminApp = lazy(() => import("./admin/AdminApp").then((m) => ({ default: m.AdminApp })));
const PublicBoutiqueView = lazy(() => import("./views/PublicBoutiqueView").then((m) => ({ default: m.PublicBoutiqueView })));
const BlogListView = lazy(() => import("./views/BlogListView").then((m) => ({ default: m.BlogListView })));
const BlogPostView = lazy(() => import("./views/BlogPostView").then((m) => ({ default: m.BlogPostView })));
const ShopHomeView = lazy(() => import("./views/ShopHomeView").then((m) => ({ default: m.ShopHomeView })));
const RemoteConnectView = lazy(() => import("./views/RemoteConnectView").then((m) => ({ default: m.RemoteConnectView })));
const ConfidentialiteView = lazy(() => import("./views/ConfidentialiteView").then((m) => ({ default: m.ConfidentialiteView })));
const CGUView = lazy(() => import("./views/CGUView").then((m) => ({ default: m.CGUView })));

import { LoginPage } from "./auth/LoginPage";
import { RegisterPage } from "./auth/RegisterPage";
import { PricingView } from "./views/PricingView";
import { PartnershipView } from "./views/PartnershipView";
import { ForgotPasswordPage } from "./auth/ForgotPasswordPage";
import { Reset2FaPage } from "./auth/Reset2FaPage";
import { AuthModalProvider, useAuthModal } from "./auth/AuthModalContext";
import { initPWA } from "./pwa";
import { AppOpeningSplash } from "./components/ui/AppOpeningSplash";

function BoutiqueSkeletonLoader() {
  return (
    <div className="min-h-screen bg-[#EBF2F7] text-slate-900 pb-24 font-sans select-none animate-pulse">
      <div className="max-w-md mx-auto px-4 pt-4 sm:pt-6">
        <div className="flex items-center justify-between py-2">
          <div className="w-12 h-12 rounded-2xl bg-slate-200/90" />
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-slate-200/90" />
            <div className="w-10 h-10 rounded-full bg-slate-200/90" />
          </div>
        </div>
        <div className="pt-2 pb-5 space-y-2.5">
          <div className="w-28 h-6 rounded-full bg-slate-200/90" />
          <div className="w-3/4 h-9 rounded-2xl bg-slate-300/80" />
          <div className="w-full h-4 rounded-lg bg-slate-200/70" />
        </div>
        <div className="flex items-center gap-2.5 overflow-hidden pb-4 pt-1">
          <div className="w-20 h-10 rounded-full bg-slate-300/90 shrink-0" />
          <div className="w-28 h-10 rounded-full bg-slate-200/90 shrink-0" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:gap-5 mt-2">
          <div className="relative rounded-[34px] p-6 shadow-xs overflow-hidden bg-slate-200/60 min-h-[195px] flex items-center justify-between gap-3">
            <div className="space-y-3 flex-1">
              <div className="w-3/4 h-6 rounded-xl bg-slate-300/70" />
              <div className="w-1/3 h-4 rounded-lg bg-slate-300/60" />
              <div className="w-1/2 h-8 rounded-xl bg-slate-300/70 mt-3" />
            </div>
            <div className="w-[48%] max-w-[195px] aspect-square rounded-[26px] bg-slate-300/70 shrink-0 transform rotate-[4.5deg]" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Activation du Service Worker PWA et auto-update
initPWA();

function PageLoader() {
  return <AppOpeningSplash />;
}

function PublicLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isVerifying } = useAuth() as any;
  const { handleAuthNavigate } = useAuthModal();
  const [searchParams] = useSearchParams();
  const allowLanding = searchParams.get('public') === 'true' || Boolean((location.state as any)?.manualNav);

  // Détection du mode PWA installée sur l'écran d'accueil mobile
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );

  // La redirection automatique à l'ouverture s'applique UNIQUEMENT à la PWA mobile installée :
  // Sur le navigateur web classique, / reste TOUJOURS la page vitrine sans redirection forcée !
  useEffect(() => {
    if (isStandalone && !allowLanding && !isVerifying) {
      if (isAuthenticated) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, [isStandalone, allowLanding, isVerifying, isAuthenticated, navigate]);

  // Si c'est l'application PWA autonome installée en cours de redirection :
  if (isStandalone && !allowLanding) {
    return <PageLoader />;
  }

  // Navigateur web normal (ordinateur ou mobile Chrome/Safari) :
  // Affichage direct et naturel de la page d'accueil vitrine
  return (
    <div className="w-full min-h-screen bg-white">
      <LandingView
        onNavigate={(tab) => {
          if (handleAuthNavigate(tab)) return;
          React.startTransition(() => {
            if (tab === 'pricing') navigate('/pricing');
            else if (tab === 'dashboard') navigate('/dashboard');
            else if (tab === 'partenariat') navigate('/partenariat');
            else navigate(`/${tab}`);
          });
        }}
      />
    </div>
  );
}

function PublicPricing() {
  const navigate = useNavigate();
  const { handleAuthNavigate } = useAuthModal();
  return (
    <div className="w-full min-h-screen bg-white">
      <PricingView
        onNavigate={(tab) => {
          if (handleAuthNavigate(tab)) return;
          React.startTransition(() => {
            if (tab === 'landing') navigate('/', { state: { manualNav: true } });
            else if (tab === 'dashboard') navigate('/dashboard');
            else if (tab === 'partenariat') navigate('/partenariat');
            else navigate(`/${tab}`);
          });
        }}
      />
    </div>
  );
}

function PublicPartnership() {
  const navigate = useNavigate();
  const { handleAuthNavigate } = useAuthModal();
  return (
    <div className="w-full min-h-screen bg-[#F8FAFC]">
      <PartnershipView
        onNavigate={(tab) => {
          if (handleAuthNavigate(tab)) return;
          React.startTransition(() => {
            if (tab === 'landing') navigate('/', { state: { manualNav: true } });
            else if (tab === 'pricing') navigate('/pricing');
            else if (tab === 'dashboard') navigate('/dashboard');
            else if (typeof tab === 'string' && tab.startsWith('/')) navigate(tab);
            else navigate(`/${tab}`);
          });
        }}
      />
    </div>
  );
}

function PricingRoute() {
  const { isAuthenticated } = useAuth() as any;
  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    );
  }
  return <PublicPricing />;
}

function LegalWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { handleAuthNavigate } = useAuthModal();
  return (
    <div className="w-full min-h-screen bg-white relative overflow-x-hidden flex flex-col justify-between">
      <PublicHeader
        onNavigate={(tab) => {
          if (handleAuthNavigate(tab)) return;
          if (tab === 'landing') navigate('/', { state: { manualNav: true } });
          else if (tab === 'pricing') navigate('/pricing');
          else if (tab === 'dashboard') navigate('/dashboard');
          else navigate(`/${tab}`);
        }}
        activeTab="legal"
      />
      <main className="pt-20 sm:pt-24 pb-16 flex-1 relative z-10">
        {children}
      </main>
      <footer className="border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/logo.webp" alt="Zeyilo" width={320} height={189} loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
            <span>— L'Agent IA WhatsApp des entreprises modernes</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="/partenariat" className="hover:text-slate-900 transition-colors">
              Partenariat
            </a>
            <a href="/confidentialite" className="hover:text-slate-900 transition-colors">
              Confidentialité
            </a>
            <a href="/cgu" className="hover:text-slate-900 transition-colors">
              Conditions Générales
            </a>
            <button
              onClick={() => navigate('/pricing')}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Tarifs
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Composant de protection : redirige automatiquement vers /login au lieu d'une page blanche
// et affiche instantanément le contenu sans squelette statique intermédiaire
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isVerifying, user } = useAuth() as any;
  const location = useLocation();

  if (isVerifying && !user) {
    return <PageLoader />;
  }

  if (!isAuthenticated && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
}

// Wrapper pour les pages d'authentification (si déjà connecté -> page demandée ou /dashboard)
// Affiche directement la page sans squelette intermédiaire pour une ouverture instantanée
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isVerifying } = useAuth() as any;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasResetToken = searchParams.has('token');

  // Si l'utilisateur est connecté mais clique sur un lien de récupération 2FA (?token=...), on ne le redirige pas
  if (!isVerifying && isAuthenticated && !hasResetToken) {
    const fromState = (location.state as any)?.from;
    let target = '/dashboard';
    if (fromState) {
      if (typeof fromState === 'string') target = fromState;
      else if (fromState.pathname) target = `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
    } else {
      const queryRedirect = searchParams.get('redirect') || searchParams.get('returnTo');
      if (queryRedirect && queryRedirect.startsWith('/')) target = queryRedirect;
    }
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

// Routeur intelligent de connexion : bascule automatiquement sur Reset2FaPage si un jeton ?token=... est fourni
function LoginRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (token) {
    return <Reset2FaPage token={token} onGoLogin={() => navigate('/login')} />;
  }

  return (
    <LoginPage
      onGoRegister={() => navigate('/register')}
      onGoForgot={() => navigate('/forgot')}
      onGoReset2Fa={() => navigate('/reset-2fa')}
    />
  );
}

const RESERVED_ROOT_PATHS = new Set([
  'dashboard', 'discussions', 'inbox', 'interventions', 'whatsapp', 'campaigns',
  'contacts', 'clients', 'agent', 'knowledge', 'settings', 'appointments',
  'operations', 'support', 'billing', 'recharge', 'partenariat', 'partenaires',
  'partners', 'affiliation', 'tarifs', 'pricing', 'blog', 'articles', 'confidentialite',
  'cgu', 'connect', 'login', 'register', 'forgot', 'forgot-password', 'reset-2fa',
  'admin', '404', 'landing', 'boutique', 'shop', 'api', 'assets', 'favicon.ico',
  'p', 'join', 'r'
]);

function DynamicRootResolver() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathFirstSegment = location.pathname.split('/')[1]?.toLowerCase() || '';

  // Si c'est vide ou racine, afficher la page vitrine publique
  if (!pathFirstSegment) {
    return <PublicLanding />;
  }

  // Redirection explicite vers l'espace studio vidéo / motion design
  if (['video', 'videos', 'tiktok', 'motion'].includes(pathFirstSegment)) {
    window.location.replace('/videos/');
    return null;
  }

  // Si c'est un segment réservé au dashboard ou pages système internes
  if (RESERVED_ROOT_PATHS.has(pathFirstSegment)) {
    return (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    );
  }

  // Sur le domaine dédié aux boutiques (shop.zeyilo.com), c'est une boutique publique sous /:slug
  if (isShopHost()) {
    return (
      <Suspense fallback={<BoutiqueSkeletonLoader />}>
        <PublicBoutiqueView />
      </Suspense>
    );
  }

  // En environnement de développement (dev.zeyilo.com, localhost), si quelqu'un accède à /:slug,
  // rediriger automatiquement vers /shop/:slug pour éviter toute erreur 404
  if (isDevEnvironment() && pathFirstSegment) {
    return <Navigate to={`/shop/${pathFirstSegment}`} replace />;
  }

  // Sur zeyilo.com en production, tout chemin inconnu est une 404 (Page introuvable), pas une boutique
  return <ErrorFallback is404 resetErrorBoundary={() => navigate('/')} />;
}

function PartnerReferralLanding() {
  const { code } = useParams<{ code?: string }>();

  useEffect(() => {
    if (code && code.trim()) {
      const cleanRef = code.trim().toUpperCase();
      localStorage.setItem('zeyilo_partner_ref', cleanRef);

      // Anti-doublon géré côté serveur (même IP + navigateur sur 24h)
      {
        fetch(`${API_BASE}/partnership/track-click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partnerCode: cleanRef, code: cleanRef, referer: document.referrer || window.location.href })
        }).catch(() => {});
      }

      // Nettoie discrètement l'URL dans la barre d'adresse pour afficher la racine https://zeyilo.com
      // sans recharger la page, offrant une confiance totale à l'utilisateur
      try {
        window.history.replaceState({}, '', '/');
      } catch {}
    }
  }, [code]);

  return <PublicLanding />;
}

function Root() {
  const navigate = useNavigate();

  // Détection et enregistrement automatique de l'affilié / partenaire créateur via l'URL (?ref=CODE)
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const refParam = searchParams.get('ref') || searchParams.get('partner');
      if (refParam && refParam.trim()) {
        const cleanRef = refParam.trim().toUpperCase();
        localStorage.setItem('zeyilo_partner_ref', cleanRef);

        // Enregistrer le clic (anti-doublon géré côté serveur : même IP + navigateur sur 24h)
        {
          fetch(`${API_BASE}/partnership/track-click`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ partnerCode: cleanRef, code: cleanRef, referer: document.referrer || window.location.href })
          }).catch(() => {});
        }

        // Nettoyage de l'URL pour supprimer ?ref= et rassurer le visiteur
        try {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl || '/');
        } catch {}
      }
    } catch {}
  }, []);

  return (
    <Routes>
      {/* Redirection et capture discrète des liens partenaires (/p/CODE ou /join/CODE) */}
      <Route path="/p/:code" element={<PartnerReferralLanding />} />
      <Route path="/join/:code" element={<PartnerReferralLanding />} />
      <Route path="/r/:code" element={<PartnerReferralLanding />} />

      {/* Pages publiques à affichage instantané sans aucun squelette animé */}
      <Route path="/" element={<PublicLanding />} />
      <Route path="/landing" element={<Navigate to="/" replace />} />
      <Route path="/pricing" element={<PricingRoute />} />
      <Route path="/partenariat" element={<PublicPartnership />} />
      <Route path="/partenaires" element={<Navigate to="/partenariat" replace />} />
      <Route path="/partners" element={<Navigate to="/partenariat" replace />} />
      <Route path="/affiliation" element={<Navigate to="/partenariat" replace />} />

      {/* Blog & Articles SEO Afrique Francophone */}
      <Route path="/blog" element={<BlogListView />} />
      <Route path="/blog/:slug" element={<BlogPostView />} />
      <Route path="/articles" element={<Navigate to="/blog" replace />} />
      <Route path="/confidentialite" element={<LegalWrapper><ConfidentialiteView /></LegalWrapper>} />
      <Route path="/cgu" element={<LegalWrapper><CGUView /></LegalWrapper>} />
      <Route path="/connect/:token" element={<RemoteConnectView />} />
      <Route path="/boutique" element={<ShopHomeView />} />
      <Route path="/shop" element={<ShopHomeView />} />
      <Route path="/explore" element={<ShopHomeView />} />
      <Route path="/boutique/:slug" element={<Suspense fallback={<BoutiqueSkeletonLoader />}><PublicBoutiqueView /></Suspense>} />
      <Route path="/shop/:slug" element={<Suspense fallback={<BoutiqueSkeletonLoader />}><PublicBoutiqueView /></Suspense>} />

      {/* Pages d'authentification */}
      <Route path="/login" element={<AuthRoute><LoginRoute /></AuthRoute>} />
      <Route path="/reset-2fa" element={<AuthRoute><Reset2FaPage onGoLogin={() => navigate('/login')} /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage onGoLogin={() => navigate('/login')} /></AuthRoute>} />
      <Route path="/forgot" element={<AuthRoute><ForgotPasswordPage onGoLogin={() => navigate('/login')} /></AuthRoute>} />
      <Route path="/forgot-password" element={<AuthRoute><ForgotPasswordPage onGoLogin={() => navigate('/login')} /></AuthRoute>} />

      {/* Console Administrateur (Accessible via /admin en développement, bloquée en production hors domaine dédié) */}
      <Route
        path="/admin"
        element={isDevEnvironment() ? <Suspense fallback={null}><AdminApp /></Suspense> : <Navigate to="/" replace />}
      />
      <Route
        path="/admin/*"
        element={isDevEnvironment() ? <Suspense fallback={null}><AdminApp /></Suspense> : <Navigate to="/" replace />}
      />

      {/* Page 404 dédiée */}
      <Route path="/404" element={<ErrorFallback is404 resetErrorBoundary={() => navigate('/dashboard')} />} />

      {/* Toutes les autres pages : tableau de bord interne ou boutique publique direct sous / */}
      <Route
        path="/*"
        element={<DynamicRootResolver />}
      />
    </Routes>
  );
}

class GlobalErrorBoundary extends (React.Component as any) {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("[GlobalErrorBoundary caught an error]:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={() => {
            this.setState({ hasError: false, error: null });
            window.location.reload();
          }}
        />
      );
    }

    return this.props.children;
  }
}

const ROUTE_SEO_META: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Zeyilo — Assistant IA WhatsApp pour Entreprises & Boutiques",
    description: "Automatisez vos ventes et votre support WhatsApp 24h/24 avec l'IA. Réponses instantanées, compréhension des vocaux et paiement Mobile Money (Wave, Orange Money, MTN, Moov)."
  },
  "/register": {
    title: "Inscription gratuite — Zeyilo",
    description: "Créez votre compte Zeyilo gratuitement sans carte bancaire et déployez votre vendeur IA WhatsApp en 2 minutes."
  },
  "/pricing": {
    title: "Tarifs & Forfaits — Zeyilo",
    description: "Découvrez les tarifs Zeyilo : plan gratuit sans carte bancaire, puis forfaits accessibles en FCFA payables par Wave, Orange Money, MTN et Moov."
  },
  "/login": {
    title: "Connexion — Zeyilo",
    description: "Connectez-vous à votre espace Zeyilo pour piloter votre agent IA WhatsApp, vos conversations clients et vos commandes."
  },
  "/partenariat": {
    title: "Programme Partenaires & Affiliation — Zeyilo",
    description: "Devenez partenaire affilié Zeyilo et gagnez des commissions récurrentes en recommandant notre assistant IA WhatsApp."
  },
  "/blog": {
    title: "Blog & Guides Commerce WhatsApp — Zeyilo",
    description: "Conseils pratiques, guides et stratégies pour automatiser vos ventes sur WhatsApp et encaisser par Mobile Money en Afrique."
  },
  "/cgu": {
    title: "Conditions Générales d'Utilisation — Zeyilo",
    description: "Consultez les Conditions Générales d'Utilisation de la plateforme Zeyilo."
  },
  "/confidentialite": {
    title: "Politique de Confidentialité — Zeyilo",
    description: "Découvrez comment Zeyilo protège la confidentialité et la sécurité de vos données et de vos échanges WhatsApp."
  }
};

function AnalyticsListener() {
  const location = useLocation();

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const isDev = host.startsWith('dev.') || host.includes('localhost') || host.includes('127.0.0.1') || window.location.port === '4000';

    // Verrouillage anti-indexation dynamique sur environnement de test
    if (isDev) {
      const robotsEl = document.querySelector('meta[name="robots"]') || document.createElement('meta');
      robotsEl.setAttribute('name', 'robots');
      robotsEl.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate');
      document.head.appendChild(robotsEl);

      const googlebotEl = document.querySelector('meta[name="googlebot"]') || document.createElement('meta');
      googlebotEl.setAttribute('name', 'googlebot');
      googlebotEl.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate');
      document.head.appendChild(googlebotEl);
    }

    // Mise à jour dynamique du <title>, <meta name="description"> et <link rel="canonical">
    // indispensable pour que Google indexe chaque page séparément et affiche les Sitelinks (Inscription, Tarifs, Connexion...)
    const cleanPath = location.pathname === "/landing" ? "/" : location.pathname;
    const meta = ROUTE_SEO_META[cleanPath];
    if (meta) {
      document.title = meta.title;
      const descEl = document.querySelector('meta[name="description"]');
      if (descEl) descEl.setAttribute("content", meta.description);
      const ogTitleEl = document.querySelector('meta[property="og:title"]');
      if (ogTitleEl) ogTitleEl.setAttribute("content", meta.title);
      const ogDescEl = document.querySelector('meta[property="og:description"]');
      if (ogDescEl) ogDescEl.setAttribute("content", meta.description);
    }

    const canonicalUrl = `https://zeyilo.com${cleanPath === "/" ? "/" : cleanPath}`;
    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", canonicalUrl);
    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    if (ogUrlEl) ogUrlEl.setAttribute("content", canonicalUrl);

    // Ne pas tracer la console d'administration interne ni l'environnement de test
    if (location.pathname.startsWith('/admin') || isDev) return;

    siteAnalytics.init();
    adPixels.init();
    const timer = setTimeout(() => {
      siteAnalytics.setupSectionObserver();
    }, 300);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}

// Désactive la restauration de défilement native du navigateur pour les transitions SPA,
// évitant que le navigateur ne replace l'utilisateur en bas ou au milieu lors d'un changement de route
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    // Si une ancre (#features, #faq, etc.) est demandée, laisser le scroll vers l'ancre se faire
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }

    // Réinitialisation instantanée et systématique du défilement au sommet de la page
    window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Réinitialiser également les conteneurs de défilement internes éventuels
    const scrollables = document.querySelectorAll<HTMLElement>('main, .overflow-y-auto');
    scrollables.forEach((el) => {
      if (el.scrollTop !== 0) el.scrollTop = 0;
    });

    // Double sécurité au frame suivant pour contrer les chargements et re-layouts asynchrones
    const frameId = requestAnimationFrame(() => {
      if (!hash) {
        window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [pathname, search, hash]);

  return null;
}

function MainAppShell() {
  if (isAdminHost()) {
    return (
      <Suspense fallback={null}>
        <AdminApp />
      </Suspense>
    );
  }

  if (isShopHost()) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<ShopHomeView />} />
          <Route path="/explore" element={<ShopHomeView />} />
          <Route path="/boutique" element={<ShopHomeView />} />
          <Route path="/shop" element={<ShopHomeView />} />
          <Route path="/boutique/:slug" element={<PublicBoutiqueView />} />
          <Route path="/shop/:slug" element={<PublicBoutiqueView />} />
          <Route path="/:slug" element={<PublicBoutiqueView />} />
          <Route path="*" element={<ShopHomeView />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <AuthModalProvider>
        <AnalyticsListener />
        <Suspense fallback={null}>
          <Root />
        </Suspense>
      </AuthModalProvider>
    </AuthProvider>
  );
}

const container = document.getElementById("root")!;
// Évite le warning createRoot() lors des rechargements HMR en développement
// @ts-ignore
const root = (window.__ZEYILO_ROOT__ ??= createRoot(container));
root.render(
  <GlobalErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <MainAppShell />
    </BrowserRouter>
  </GlobalErrorBoundary>
);
